/**
 * Streaming file upload helper for admin backend.
 *
 * Uploads raw binary (`application/octet-stream`) directly to
 * `POST /api/files/admin/upload` via XMLHttpRequest to support
 * multi-gigabyte files without buffering the whole body in RAM.
 *
 * Why XHR instead of fetch: the `fetch` Request API does not emit
 * upload progress events — there is no portable way to observe
 * `upload.onprogress`. XHR remains the only browser-native API with
 * progress reporting.
 *
 * Auth: admin panel uses JWT stored in `localStorage.accessToken`.
 * The global fetch interceptor (see `lib/fetch-with-auth.ts`) only
 * intercepts `fetch`, not XHR, so we attach the header manually.
 */

import { resolveApiUrl } from '@/providers/api-url';
import { getActiveTenant } from '@/lib/active-tenant';

export type FileAccess = 'PUBLIC' | 'PRIVATE';

export interface UploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

export interface UploadFileOptions {
  file: File;
  access?: FileAccess;
  onProgress?: (progress: UploadProgress) => void;
  signal?: AbortSignal;
}

export interface UploadFileResult {
  id: number;
  url: string;
  storagePath: string;
  status: string;
}

interface UploadApiResponse {
  success: boolean;
  data: UploadFileResult | null;
  errors: string[] | null;
}

function isUploadApiResponse(value: unknown): value is UploadApiResponse {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as { success?: unknown; data?: unknown; errors?: unknown };
  return typeof candidate.success === 'boolean';
}

function isUploadFileResult(value: unknown): value is UploadFileResult {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as {
    id?: unknown;
    url?: unknown;
    storagePath?: unknown;
    status?: unknown;
  };
  return (
    typeof candidate.id === 'number' &&
    typeof candidate.url === 'string' &&
    typeof candidate.storagePath === 'string' &&
    typeof candidate.status === 'string'
  );
}

/**
 * Upload a single `File` as raw binary body to `/files/admin/upload`.
 *
 * The function resolves with the created file record returned by the
 * backend, or rejects with a typed `Error` on any failure (network,
 * HTTP, abort, timeout, bad response shape).
 */
export function uploadFile(options: UploadFileOptions): Promise<UploadFileResult> {
  const { file, access = 'PUBLIC', onProgress, signal } = options;

  return new Promise<UploadFileResult>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error('Upload aborted'));
      return;
    }

    const apiUrl = resolveApiUrl();
    const endpoint = `${apiUrl}/files/admin/upload`;

    const xhr = new XMLHttpRequest();
    xhr.open('POST', endpoint, true);

    // Raw binary streaming contract — backend reads the request body
    // as a stream, without multipart parsing.
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.setRequestHeader('X-File-Name', encodeURIComponent(file.name));
    xhr.setRequestHeader(
      'X-File-Type',
      file.type.length > 0 ? file.type : 'application/octet-stream',
    );
    xhr.setRequestHeader('X-File-Access', access);

    // JWT из localStorage — global fetch interceptor не перехватывает XHR,
    // поэтому добавляем Authorization header вручную.
    const token = localStorage.getItem('accessToken');
    if (token !== null && token.length > 0) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    // X-Tenant-Id — interceptor добавляет его на fetch, но XHR его минует.
    // Без заголовка backend для admin-пути падает в default-тенант (centr) →
    // на узб (club) загрузка ломалась. Однотенантный режим: tenant=null, не шлём.
    const tenant = getActiveTenant();
    if (tenant !== null && tenant.length > 0) {
      xhr.setRequestHeader('X-Tenant-Id', tenant);
    }

    // refresh-токен лежит в httpOnly cookie — нужны cookies при cross-origin.
    xhr.withCredentials = true;

    if (onProgress) {
      xhr.upload.onprogress = (event: ProgressEvent): void => {
        if (!event.lengthComputable) {
          return;
        }
        const total = event.total > 0 ? event.total : file.size;
        const percent = total > 0 ? Math.round((event.loaded / total) * 100) : 0;
        onProgress({ loaded: event.loaded, total, percent });
      };
    }

    xhr.onload = (): void => {
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
        return;
      }

      let parsed: unknown;
      try {
        parsed = JSON.parse(xhr.responseText);
      } catch {
        reject(new Error('Invalid response from server'));
        return;
      }

      if (!isUploadApiResponse(parsed)) {
        reject(new Error('Invalid response shape from server'));
        return;
      }

      if (!parsed.success || parsed.data === null) {
        const message =
          parsed.errors !== null && parsed.errors.length > 0
            ? parsed.errors.join(', ')
            : 'Upload failed';
        reject(new Error(message));
        return;
      }

      if (!isUploadFileResult(parsed.data)) {
        reject(new Error('Invalid file record in server response'));
        return;
      }

      resolve(parsed.data);
    };

    xhr.onerror = (): void => {
      reject(new Error('Network error during upload'));
    };
    xhr.onabort = (): void => {
      reject(new Error('Upload aborted'));
    };

    if (signal) {
      const onAbort = (): void => {
        xhr.abort();
      };
      signal.addEventListener('abort', onAbort, { once: true });
    }

    // Raw File объект — браузер сам выставит Content-Length и стримит тело.
    xhr.send(file);
  });
}
